// functions/lib/ugc/rules.ts
//
// Règles du programme UGC (spec § 3). Seul endroit où vivent ces nombres : le serveur,
// le robot et le portail les lisent ici. Montants en centimes entiers ; dates calendaires
// YYYY-MM-DD en heure de Paris. Le montant pour 1 000 vues n'est pas une règle commune : l'équipe
// le fixe pour chaque membre (`ugc_members.rate_cents_per_thousand`, migration 0021) ; le seuil, le
// plafond et le quota restent communs à tout le programme.

import type { UgcPublication } from '../../../src/lib/ugcTypes'
import { addDays, parisDate } from '../report/dates'

export const UGC_RULES = {
  /**
   * Seuil minimal avant rémunération : moins de 10 000 vues, la vidéo n'est pas rémunérée ; à partir
   * de 10 000, toutes ses vues sont payées.
   */
  thresholdViews: 10_000,
  /** Vues payées au plus par vidéo (au montant pour 1 000 vues de la personne). */
  capViews: 150_000,
  /** Montants positifs payés au plus par personne et par mois de publication. */
  monthlyPaidQuota: 4,
  /** Prime par personne parrainée, quand sa première vidéo est payée. */
  referralBonusCents: 2_000,
  /** Relevé des vues au 30e jour après la publication. */
  readingDelayDays: 30,
  /** Durée minimale en ligne. */
  minOnlineDays: 60,
  /** Délai pour publier la même vidéo sur la seconde plateforme. */
  secondPlatformWindowDays: 7,
  /** Rappels de capture, en jours après la date de relevé (30e, 33e et 37e jours). */
  reminderOffsets: [0, 3, 7],
  /** « Capture manquante » dès le 37e jour après la publication. */
  missingAfterDays: 37,
  /** Réponse promise à une idée envoyée (spec § 5.3). */
  ideaReplyHours: 48,
  /** Réponse promise à une question écrite dans le chat avec l'équipe (ou à l'adresse de contact). */
  chatReplyHours: 48,
  /**
   * Repère de l'équipe pour relire un brouillon : au-delà, la file de l'admin et l'e-mail du matin le
   * marquent « en retard ». Jamais annoncé à la personne (« nous te répondons vite »).
   */
  draftReplyHours: 48,
  /** Activation du Pro promise après que le membre a donné l'adresse de son compte dans l'app. */
  proActivationHours: 48,
  /**
   * E-mails de la conversation d'une vidéo (Resend en offre gratuite : 100 e-mails par jour,
   * partagés avec les codes de connexion de l'app) : au plus un par vidéo et par sens dans cette
   * fenêtre, en minutes ; la personne lit la suite dans sa conversation.
   */
  messageEmailWindowMinutes: 30,
  /**
   * E-mails de conversation par jour (heure de Paris), alertes, e-mails aux créateurs et résumés du
   * matin confondus.
   */
  messageEmailsPerDay: 40,
  /**
   * Résumés du matin (« Tes messages non lus ») : ils ne partent que tant que ce nombre d'e-mails
   * de conversation n'est pas atteint dans la journée, pour laisser au moins 10 des 40 aux e-mails
   * immédiats et aux alertes. Les résumés qui ne partent pas attendent le lendemain.
   */
  messageDigestEmailsPerDay: 30,
} as const

/**
 * Grille figée avec chaque montant : un changement de grille, ou du montant pour 1 000 vues d'une
 * personne, ne touche pas les anciens montants.
 */
export type RuleSnapshot = { rateCentsPerThousand: number; thresholdViews: number; capViews: number }

/**
 * Bornes du montant pour 1 000 vues fixé par l'équipe, en centimes (même contrôle que la base,
 * migration 0021) : de 1 centime à 100 €, pour refuser une faute de frappe (250 € au lieu de 2,50 €).
 */
export const MEMBER_RATE_MIN_CENTS = 1
export const MEMBER_RATE_MAX_CENTS = 10_000

/** Vrai pour un montant pour 1 000 vues acceptable : entier, entre les deux bornes. */
export function isValidMemberRate(cents: number): boolean {
  return Number.isInteger(cents) && cents >= MEMBER_RATE_MIN_CENTS && cents <= MEMBER_RATE_MAX_CENTS
}

/** Grille d'une personne : son montant pour 1 000 vues, le seuil et le plafond du programme. */
export function memberRules(rateCentsPerThousand: number): RuleSnapshot {
  return { rateCentsPerThousand, thresholdViews: UGC_RULES.thresholdViews, capViews: UGC_RULES.capViews }
}

/**
 * Versions datées des conditions et de la politique de confidentialité, au format AAAA-MM-JJ : la
 * date est enregistrée au consentement et affichée « Version du 4 novembre 2026 » (une nouvelle
 * version de texte prend une nouvelle date, jamais celle d'une version déjà en ligne).
 */
export const LEGAL_VERSIONS = { terms: '2026-11-05', privacy: '2026-11-05' } as const

/** Mot de passe facultatif de l'espace : assez long pour résister, assez court pour un téléphone. */
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

/** Message de la conversation d'une vidéo, après nettoyage : de quoi répondre vraiment, pas plus. */
export const MESSAGE_MAX_LENGTH = 2000

/** Messages qu'un membre écrit au plus par heure, toutes ses vidéos et son chat avec l'équipe compris (au-delà : 429). */
export const MEMBER_MESSAGES_PER_HOUR = 30

/**
 * Versions qu'une personne peut joindre à son idée tant qu'elle n'est pas validée (au-delà : 409
 * `too_many_drafts`) ; une fois l'idée validée, plus de plafond.
 */
export const EARLY_DRAFTS_MAX = 3

/** Versions qu'un membre peut retirer par heure (au-delà : 429). */
export const DRAFT_WITHDRAWALS_PER_HOUR = 10

/** Modifications d'une idée encore en relecture, par heure et par membre (au-delà : 429). */
export const IDEA_EDITS_PER_HOUR = 10

/** Changements des comptes TikTok et Instagram (page Profil) par heure et par membre (au-delà : 429). */
export const PROFILE_CHANGES_PER_HOUR = 10

/** Changements de l'adresse du compte dans l'app par jour et par membre (au-delà : 429). */
export const APP_ACCOUNT_CHANGES_PER_DAY = 3

/** Nouvelles demandes de Pro (« Redemander le Pro ») par jour et par membre (au-delà : 429). */
export const PRO_REQUESTS_PER_DAY = 3

/** Changements du choix des e-mails de messages par heure et par membre (au-delà : 429). */
export const EMAIL_DIGEST_CHANGES_PER_HOUR = 20

/** Points d'une relecture (« Ce qui marche », « À retoucher ») : 8 par liste au plus, 160 caractères chacun. */
export const REVIEW_POINTS_MAX = 8
export const REVIEW_POINT_MAX_LENGTH = 160

/**
 * Montant d'une vidéo en centimes, pour le total de ses vues (toutes plateformes), arrondi au centime,
 * avec la grille donnée (celle de la personne, `memberRules`, ou celle figée avec un montant).
 */
export function amountCents(views: number, rules: RuleSnapshot): number {
  if (!Number.isInteger(views) || views < 0) {
    throw new RangeError(`amountCents : nombre de vues invalide (${views})`)
  }
  if (views < rules.thresholdViews) return 0
  // Numérateur entier : la division par 1 000 tombe juste sur les demis, l'arrondi est fiable.
  return Math.round((Math.min(views, rules.capViews) * rules.rateCentsPerThousand) / 1000)
}

/** Date du relevé (30e jour). */
export function readingDueOn(publishedOn: string): string {
  return addDays(publishedOn, UGC_RULES.readingDelayDays)
}

/** Dernier jour où la vidéo doit encore être en ligne (60e jour). */
export function onlineUntil(publishedOn: string): string {
  return addDays(publishedOn, UGC_RULES.minOnlineDays)
}

/** Jour à partir duquel une publication sans capture passe en « Capture manquante » (37e jour). */
export function missingFrom(publishedOn: string): string {
  return addDays(publishedOn, UGC_RULES.missingAfterDays)
}

/** « Capture manquante » : en ligne sans capture à partir du 37e jour (heure de Paris). */
export function isMissing(publication: Pick<UgcPublication, 'status' | 'publishedOn'>, now: Date): boolean {
  return publication.status === 'live' && parisDate(now) >= missingFrom(publication.publishedOn)
}

/** Mois du quota (YYYY-MM) : celui de la publication la plus ancienne de la vidéo. */
export function payoutMonth(publishedOns: readonly string[]): string {
  const first = [...publishedOns].sort()[0]
  if (first === undefined) throw new RangeError('payoutMonth : aucune date de publication')
  return first.slice(0, 7)
}

/** Vrai si le mois compte déjà assez de montants positifs figés : le suivant n'est pas payé. */
export function isOverQuota(positiveFrozenBefore: number): boolean {
  return positiveFrozenBefore >= UGC_RULES.monthlyPaidQuota
}

const EUROS = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })

/** Centimes en euros à la française (« 1 260,00 € », espaces insécables). */
export function formatEuros(cents: number): string {
  return EUROS.format(cents / 100)
}
